// La misma persona de PostHog al pasar de Instagram a Chrome.
//
// Al salir del navegador de la app (el modal de Google, o el "⋯ → Abrir en el navegador"),
// Chrome arranca sin cookies: PostHog lo contaba como una persona NUEVA y no se podía
// saber si quien tocó "Continuar" terminó registrándose. Los ids de la visita viajan en el
// link y Chrome arranca con ellos (`bootstrap` de `initPostHog`)
// (ADR-2026-11-03-posthog-sigue-a-la-persona-en-el-navegador).
//
// Puro, para poder testearlo: lo que toca `window` y el SDK vive en `lib/posthog.ts`.

export const PARAM_DISTINCT_ID = 'ph_did';
export const PARAM_SESSION_ID = 'ph_sid';

export interface IdsDeVisita {
  distinctId: string;
  /** UUID v7 de la sesión: con él el replay de Chrome sigue al de Instagram. */
  sessionId: string | null;
}

// Un distinct_id de PostHog es un UUID (anónimo) o el de Supabase (logueado). Se acota por
// si alguien arma el link a mano: nada raro llega al `init`.
const DISTINCT_ID_VALIDO = /^[\w.:@-]{1,128}$/;
// PostHog exige que el sessionID del bootstrap sea un UUID v7.
const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** La URL con los ids de la visita, para el navegador al que se salta. */
export function conIdsDeVisita(href: string, ids: IdsDeVisita): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  if (!DISTINCT_ID_VALIDO.test(ids.distinctId)) return href;
  url.searchParams.set(PARAM_DISTINCT_ID, ids.distinctId);
  if (ids.sessionId && UUID_V7.test(ids.sessionId)) url.searchParams.set(PARAM_SESSION_ID, ids.sessionId);
  else url.searchParams.delete(PARAM_SESSION_ID);
  return url.toString();
}

/**
 * Los ids que trajo el link, y la URL SIN ellos: se sacan siempre (válidos o no), así lo
 * que se copie de la barra de Chrome ya no los lleva. Lo que se copie de la barra de
 * Instagram sí: si alguien comparte ESE link, las dos personas quedan juntas en PostHog
 * (un riesgo aceptado, ver el ADR).
 */
export function leerIdsDeVisita(href: string): { ids: IdsDeVisita | null; sinIds: string } {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { ids: null, sinIds: href };
  }
  if (!url.searchParams.has(PARAM_DISTINCT_ID) && !url.searchParams.has(PARAM_SESSION_ID)) {
    return { ids: null, sinIds: href };
  }
  const distinctId = url.searchParams.get(PARAM_DISTINCT_ID) ?? '';
  const sessionId = url.searchParams.get(PARAM_SESSION_ID);
  url.searchParams.delete(PARAM_DISTINCT_ID);
  url.searchParams.delete(PARAM_SESSION_ID);
  const ids = DISTINCT_ID_VALIDO.test(distinctId)
    ? { distinctId, sessionId: sessionId && UUID_V7.test(sessionId) ? sessionId : null }
    : null;
  return { ids, sinIds: url.toString() };
}
